import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import UploadFile from "components/_refactor/dropzone";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  MATERIAL_TYPES,
  CONTENT_STATUS,
  SEGMENTATION_OPTIONS,
  ACCEPT_IMAGES,
  ACCEPT_MATERIAL_FILES,
} from "../constants";
import {
  createContent,
  editContent,
  listCampaigns,
  buildFilePayload,
  buildSegmentacao,
  buildTags,
} from "../services/franchise-marketing-api";
import {
  populateContentForm,
  getAgendadoExtraFields,
} from "../utils/form-helpers";

const EMPTY_FORM = {
  nome: "",
  tipo: null,
  categoria: null,
  campanha: null,
  descricao: "",
  dataInicio: "",
  dataFim: "",
  status: null,
  segmentacao: null,
  tags: "",
  ordemExibicao: "",
  dataPublicacaoAgendada: "",
};

const MATERIAL_TIPO_MAP = {
  PDF: "MaterialPdf",
  JPG: "MaterialPdf",
  PNG: "MaterialPdf",
  MP4: "Video",
  LINK: "Institucional",
  ZIP: "MaterialPdf",
};

export default function DialogMaterial({
  open,
  onClose,
  onSaved,
  content,
  presetTipo,
  categoryOptions = [],
}) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [arquivoFiles, setArquivoFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [campanhaOptions, setCampanhaOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileResetBase = `${open}-${content?.IdFranquiaMarketingConteudo || content?.Id || presetTipo || "new"}`;

  const loadCampaignOptions = async () => {
    try {
      const campaigns = await listCampaigns();
      setCampanhaOptions(
        campaigns.map((c) => ({
          value: c.IdFranquiaMarketingCampanha,
          label: c.Nome,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadCampaignOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (content) {
      const populated = populateContentForm(
        content,
        categoryOptions,
        campanhaOptions,
        [],
      );
      if (populated) {
        setForm({
          ...populated,
          tipo:
            MATERIAL_TYPES.find(
              (t) => t.value === (content.TipoArquivo || "PDF").toUpperCase(),
            ) || MATERIAL_TYPES[0],
        });
      }
    }
  }, [open, content, categoryOptions, campanhaOptions]);

  useEffect(() => {
    if (!open || content) return;
    setForm({
      ...EMPTY_FORM,
      tipo: presetTipo
        ? MATERIAL_TYPES.find((t) => t.value === presetTipo) || null
        : null,
    });
    setArquivoFiles([]);
    setThumbnailFiles([]);
  }, [open, content, presetTipo]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resolveContentTipo = () => {
    if (presetTipo === "Lookbook" || content?.Tipo === "Lookbook") {
      return "Lookbook";
    }
    const fileType = form.tipo?.value;
    return MATERIAL_TIPO_MAP[fileType] || "MaterialPdf";
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome do material é obrigatório.", {
        severity: "error",
      });
      return;
    }

    if (!form.categoria?.value) {
      snackbar.showMessage("Categoria é obrigatória.", { severity: "error" });
      return;
    }

    setSaving(true);

    try {
      const thumbnailPayload = await buildFilePayload(thumbnailFiles, "thumbnail");
      const arquivoPayload = await buildFilePayload(arquivoFiles, "arquivo");

      const payload = {
        titulo: form.nome.trim(),
        descricao: form.descricao,
        tipo: resolveContentTipo(),
        idFranquiaMarketingCategoria: form.categoria.value,
        idFranquiaMarketingCampanha: form.campanha?.value || null,
        dataInicioVigencia: form.dataInicio || null,
        dataFimVigencia: form.dataFim || null,
        status: form.status?.value || "Rascunho",
        tipoArquivo: form.tipo?.value || "PDF",
        ordemExibicao: Number(form.ordemExibicao) || 0,
        tags: buildTags(form.tags),
        segmentacao: buildSegmentacao(form.segmentacao),
        ...getAgendadoExtraFields(form),
        ...thumbnailPayload,
        ...arquivoPayload,
      };

      const editId =
        content?.IdFranquiaMarketingConteudo || content?.id || content?.Id;

      if (editId) {
        await editContent({
          ...payload,
          idFranquiaMarketingConteudo: editId,
        });
        snackbar.showMessage("Material atualizado com sucesso.", {
          severity: "success",
        });
      } else {
        await createContent(payload);
        snackbar.showMessage("Material salvo com sucesso.", {
          severity: "success",
        });
      }

      onSaved?.();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao salvar material.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  const isLookbook = presetTipo === "Lookbook" || content?.Tipo === "Lookbook";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle style={{ color: themeColorsDesingSystem.text.primary }}>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content
            ? `Editar ${isLookbook ? "Lookbook" : "Material"}`
            : `Cadastro de ${isLookbook ? "Lookbook" : "Material"}`}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label={isLookbook ? "Nome do lookbook" : "Nome do material"}
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              {!isLookbook && (
                <Grid item xs={12}>
                  <AutoCompleteField
                    label="Tipo de material"
                    value={form.tipo}
                    onChange={(v) => updateForm("tipo", v)}
                    options={MATERIAL_TYPES}
                    optionLabelKey="label"
                    optionIdKey="value"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={categoryOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Campanha vinculada"
                  value={form.campanha}
                  onChange={(v) => updateForm("campanha", v)}
                  options={campanhaOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência início"
                  value={form.dataInicio}
                  onChange={(v) => updateForm("dataInicio", v)}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência fim"
                  value={form.dataFim}
                  onChange={(v) => updateForm("dataFim", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v)}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Tags"
                  value={form.tags}
                  onChange={(e) => updateForm("tags", e.target.value)}
                  placeholder="Ex: vm, merchandising, pdf"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              {form.status?.value === "Agendado" && (
                <Grid item xs={12}>
                  <InputText
                    label="Data/hora de publicação agendada"
                    type="datetime-local"
                    value={form.dataPublicacaoAgendada}
                    onChange={(e) =>
                      updateForm("dataPublicacaoAgendada", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Arquivo
                </Typography>
                <UploadFile
                  message="Arraste o arquivo ou clique para selecionar"
                  accept={ACCEPT_MATERIAL_FILES}
                  onChangeFiles={setArquivoFiles}
                  resetKey={`${fileResetBase}-arquivo`}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setThumbnailFiles}
                  resetKey={`${fileResetBase}-thumbnail`}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          Salvar {isLookbook ? "lookbook" : "material"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
